import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

import { App } from '@/services/app-compat';
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');

// Priority constants matching existing codebase
const PRIORITY_HIGH = 300;
const PRIORITY_MEDIUM = 200;
const PRIORITY_LOW = 100;

const PRIORITY_OPTIONS = [
  { code: PRIORITY_HIGH, value: 'High', class: 'priority-high' },
  { code: PRIORITY_MEDIUM, value: 'Medium', class: 'priority-medium' },
  { code: PRIORITY_LOW, value: 'Low', class: 'priority-low' },
];

interface NPSDWarningsProps {
  warnings: any[];
  isReadOnly?: boolean;
  title?: string;
  maxLength?: number;
  onChange?: (updatedWarnings: any[]) => void;
}

export function NPSDWarnings({
  warnings = [],
  isReadOnly = false,
  title = 'Warnings',
  maxLength = 255,
  onChange,
}: NPSDWarningsProps) {
  const { theme } = useTheme();
  const rootRef = useRef<HTMLDivElement>(null);
  const isEmittingRef = useRef(false);

  const [isMobile, setIsMobile] = useState(false);
  const [newWarning, setNewWarning] = useState('');
  const [newPriority, setNewPriority] = useState<number | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingWarning, setEditingWarning] = useState('');
  const [editingPriority, setEditingPriority] = useState(PRIORITY_MEDIUM);

  // Check if mobile (debounced to avoid excessive re-renders during resize)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
    };
    setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const warningCount = useMemo((): number => {
    return warnings?.length || 0;
  }, [warnings]);

  const highPriorityWarnings = useMemo((): any[] => {
    return warnings.filter((w: any) =>
      w.priority === PRIORITY_HIGH || w.priority === String(PRIORITY_HIGH)
    );
  }, [warnings]);

  const mediumPriorityWarnings = useMemo((): any[] => {
    return warnings.filter((w: any) =>
      w.priority === PRIORITY_MEDIUM || w.priority === String(PRIORITY_MEDIUM)
    );
  }, [warnings]);

  const lowPriorityWarnings = useMemo((): any[] => {
    return warnings.filter((w: any) =>
      w.priority === PRIORITY_LOW || w.priority === String(PRIORITY_LOW)
    );
  }, [warnings]);

  const canAddWarning = useMemo((): boolean => {
    return newWarning.trim().length > 0 && newPriority !== null && !isReadOnly;
  }, [newWarning, newPriority, isReadOnly]);

  const remainingChars = useMemo((): number => {
    return maxLength - newWarning.length;
  }, [maxLength, newWarning]);

  const getOriginalIndex = useCallback((warning: any): number => {
    return warnings.findIndex((w: any) =>
      (w.uuid && w.uuid === warning.uuid) ||
      (w.id && w.id === warning.id) ||
      (w.warning === warning.warning && w.priority === warning.priority)
    );
  }, [warnings]);

  const getWarningKey = useCallback((warning: any, index: number): string => {
    return warning.uuid || warning.id || `warning-${index}`;
  }, []);

  const getPriorityClass = useCallback((priority: number | string): string => {
    const numPriority = Number(priority);
    if (numPriority === PRIORITY_HIGH) return 'priority-high';
    if (numPriority === PRIORITY_MEDIUM) return 'priority-medium';
    if (numPriority === PRIORITY_LOW) return 'priority-low';
    return 'priority-medium';
  }, []);

  const getPriorityLabel = useCallback((priority: number | string): string => {
    const numPriority = Number(priority);
    if (numPriority === PRIORITY_HIGH) return 'High';
    if (numPriority === PRIORITY_MEDIUM) return 'Medium';
    if (numPriority === PRIORITY_LOW) return 'Low';
    return 'Medium';
  }, []);

  const getAlertClass = useCallback((priority: number | string): string => {
    const numPriority = Number(priority);
    if (numPriority === PRIORITY_HIGH) return 'alert-danger';
    if (numPriority === PRIORITY_MEDIUM) return 'alert-warning';
    if (numPriority === PRIORITY_LOW) return 'alert-info';
    return 'alert-warning';
  }, []);

  const saveEditWarning = useCallback(() => {
    if (editingIndex === null || isEmittingRef.current) return;
    isEmittingRef.current = true;

    const updatedWarnings = warnings.map((warning: any, i: number) => {
      if (i === editingIndex) {
        return {
          ...warning,
          warning: editingWarning.trim(),
          priority: editingPriority,
        };
      }
      return warning;
    });

    onChange?.(updatedWarnings);

    setEditingIndex(null);
    setEditingWarning('');
    setEditingPriority(PRIORITY_MEDIUM);

    setTimeout(() => {
      isEmittingRef.current = false;
    }, 0);
  }, [editingIndex, editingWarning, editingPriority, warnings, onChange]);

  const cancelEditWarning = useCallback(() => {
    setEditingIndex(null);
    setEditingWarning('');
    setEditingPriority(PRIORITY_MEDIUM);
  }, []);

  // Click outside handler for edit mode
  useEffect(() => {
    if (editingIndex === null) return;

    const handleClickOutside = (event: MouseEvent) => {
      const el = rootRef.current;
      if (!el) return;

      const editForms = el.querySelectorAll('.edit-form, .card-content');
      let clickedInside = false;

      editForms.forEach((form: Element) => {
        if (form.contains(event.target as Node)) {
          clickedInside = true;
        }
      });

      const editButton = (event.target as Element).closest('.btn-edit');
      if (editButton) {
        clickedInside = true;
      }

      if (!clickedInside) {
        saveEditWarning();
      }
    };

    // Use setTimeout to avoid immediate trigger from the click that opened the edit
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingIndex, saveEditWarning]);

  const addWarning = useCallback(() => {
    if (isReadOnly || isEmittingRef.current) return;
    if (!newWarning.trim()) {
      if (typeof App !== 'undefined' && App.showPopupNotification) {
        App.showPopupNotification("Warning text is required.", "warning");
      }
      return;
    }
    if (newPriority === null) {
      if (typeof App !== 'undefined' && App.showPopupNotification) {
        App.showPopupNotification("Please select a priority.", "warning");
      }
      return;
    }

    isEmittingRef.current = true;

    const newWarningObj = {
      uuid: getInsecureUUID(),
      id: null,
      warning: newWarning.trim(),
      priority: newPriority,
      dateCreated: new Date().toISOString(),
    };

    const updatedWarnings = [...warnings, newWarningObj];
    onChange?.(updatedWarnings);

    setNewWarning('');
    setNewPriority(null);

    setTimeout(() => {
      isEmittingRef.current = false;
      if (rootRef.current) {
        scrollToLastMobileCard(rootRef.current);
      }
    }, 0);
  }, [isReadOnly, newWarning, newPriority, warnings, onChange]);

  const removeWarning = useCallback((index: number) => {
    if (isReadOnly || isEmittingRef.current) return;
    isEmittingRef.current = true;

    const updatedWarnings = warnings.filter((_: any, i: number) => i !== index);
    onChange?.(updatedWarnings);

    setTimeout(() => {
      isEmittingRef.current = false;
    }, 0);
  }, [isReadOnly, warnings, onChange]);

  const startEditWarning = useCallback((index: number) => {
    if (isReadOnly) return;

    // If already editing another warning, save it first
    if (editingIndex !== null && editingIndex !== index) {
      saveEditWarning();
    }

    const warning = warnings[index];
    setEditingIndex(index);
    setEditingWarning(warning.warning);
    setEditingPriority(Number(warning.priority));
  }, [isReadOnly, editingIndex, warnings, saveEditWarning]);

  const addDateToWarning = useCallback(() => {
    const dateText = " - " + new Date().toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
    if (newWarning.length + dateText.length <= maxLength) {
      setNewWarning(prev => prev + dateText);
    } else {
      if (typeof App !== 'undefined' && App.showPopupNotification) {
        App.showPopupNotification("Cannot add date: Warning would exceed maximum length.", "error");
      }
    }
  }, [newWarning, maxLength]);

  const handleNewWarningKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      addWarning();
    }
  }, [addWarning]);

  // Render a warning section (High, Medium, or Low)
  const renderWarningSection = (
    sectionWarnings: any[],
    sectionKey: string,
    sectionLabel: string,
    priorityClass: string,
    alertClass: string,
    iconClass: string,
  ) => {
    if (sectionWarnings.length === 0) return null;

    return (
      <div className="warning-section" key={sectionKey}>
        <div className={`section-header ${priorityClass}`}>
          <i className={iconClass}></i>
          {sectionLabel}
          <span className="count">{sectionWarnings.length}</span>
        </div>
        <div className="warning-list">
          {sectionWarnings.map((warning) => {
            const origIdx = getOriginalIndex(warning);
            const isEditing = editingIndex === origIdx;
            return (
              <div
                key={getWarningKey(warning, origIdx)}
                className={`warning-item alert ${alertClass}${isEditing ? ' editing' : ''}`}
              >
                {isEditing ? (
                  <div className="edit-form">
                    <input
                      type="text"
                      value={editingWarning}
                      onChange={(e) => setEditingWarning(e.target.value)}
                      maxLength={maxLength}
                      className="kstc-input"
                    />
                    <div className="priority-options-inline">
                      {PRIORITY_OPTIONS.map((opt) => (
                        <label
                          key={'edit-' + opt.code}
                          className={`priority-radio-inline ${opt.class}${editingPriority === opt.code ? ' selected' : ''}`}
                        >
                          <input
                            type="radio"
                            value={opt.code}
                            checked={editingPriority === opt.code}
                            onChange={() => setEditingPriority(opt.code)}
                          />
                          <span>{opt.value}</span>
                        </label>
                      ))}
                    </div>
                    <button className="btn-save" onClick={saveEditWarning}><i className="fas fa-check"></i></button>
                    <button className="btn-cancel" onClick={cancelEditWarning}><i className="fas fa-times"></i></button>
                  </div>
                ) : (
                  <>
                    <span className="warning-text">{warning.warning}</span>
                    {!isReadOnly && (
                      <div className="warning-actions">
                        <button className="btn-edit" onClick={() => startEditWarning(origIdx)} title="Edit">
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="btn-remove" onClick={() => removeWarning(origIdx)} title="Remove">
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`npsd-warnings ${theme}`} ref={rootRef}>
      {/* Header */}
      <div className="header">
        <h1>{title}</h1>
        {warningCount > 0 && <span className="badge">{warningCount}</span>}
      </div>

      {/* Add Warning Form */}
      {!isReadOnly && (
        <div className="add-warning-form">
          <div className="form-row">
            <div className="input-wrapper">
              <input
                type="text"
                value={newWarning}
                onChange={(e) => setNewWarning(e.target.value)}
                maxLength={maxLength}
                className="kstc-input"
                placeholder="Enter warning text..."
                onKeyUp={handleNewWarningKeyUp}
              />
              <span className={`char-count${remainingChars < 20 ? ' near-limit' : ''}`}>
                {remainingChars}
              </span>
            </div>
            <button className="btn-date" onClick={addDateToWarning} title="Add current date">
              <i className="fas fa-calendar-plus"></i> Date
            </button>
          </div>
          <div className="form-row priority-row">
            <div className="priority-radio-group">
              <label className="priority-label">Priority:</label>
              <div className="priority-options">
                {PRIORITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.code}
                    type="button"
                    className={`priority-btn ${opt.class}${newPriority === opt.code ? ' selected' : ''}`}
                    onClick={() => setNewPriority(opt.code)}
                  >
                    {opt.value}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="form-row button-row">
            <button className="btn-add" onClick={addWarning} disabled={!canAddWarning}>
              <i className="fas fa-plus"></i> ADD WARNING
            </button>
          </div>
        </div>
      )}

      {/* Desktop View - Grouped by Priority */}
      <div className="warning-sections">
        {renderWarningSection(
          highPriorityWarnings, 'section-high', 'High Priority',
          'priority-high', 'alert-danger', 'fas fa-exclamation-triangle'
        )}
        {renderWarningSection(
          mediumPriorityWarnings, 'section-medium', 'Medium Priority',
          'priority-medium', 'alert-warning', 'fas fa-exclamation-circle'
        )}
        {renderWarningSection(
          lowPriorityWarnings, 'section-low', 'Low Priority',
          'priority-low', 'alert-info', 'fas fa-info-circle'
        )}

        {/* Empty State - only show in read-only mode */}
        {warningCount === 0 && isReadOnly && (
          <div className="empty-state" key="empty-state">
            <span>No warnings recorded</span>
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards">
        {warningCount === 0 && isReadOnly && (
          <div className="empty-mobile" key="empty-mobile">
            No warnings recorded
          </div>
        )}
        {warnings.map((warning, index) => {
          const isEditing = editingIndex === index;
          return (
            <div
              key={'mobile-' + getWarningKey(warning, index)}
              className={`card ${getAlertClass(warning.priority)}${isEditing ? ' editing' : ''}`}
            >
              <div className="card-header">
                <span className={`priority-badge ${getPriorityClass(warning.priority)}`}>
                  {getPriorityLabel(warning.priority)}
                </span>
                {!isReadOnly && (
                  <div className="card-actions">
                    <button className="btn-edit" onClick={() => startEditWarning(index)}>
                      <i className="fas fa-edit"></i>
                    </button>
                    <button className="btn-remove" onClick={() => removeWarning(index)}>
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                )}
              </div>
              <div className="card-content">
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      value={editingWarning}
                      onChange={(e) => setEditingWarning(e.target.value)}
                      maxLength={maxLength}
                      className="kstc-input"
                    />
                    <div className="priority-options-mobile">
                      {PRIORITY_OPTIONS.map((opt) => (
                        <label
                          key={'mobile-edit-' + opt.code}
                          className={`priority-radio-mobile ${opt.class}${editingPriority === opt.code ? ' selected' : ''}`}
                        >
                          <input
                            type="radio"
                            value={opt.code}
                            checked={editingPriority === opt.code}
                            onChange={() => setEditingPriority(opt.code)}
                          />
                          <span>{opt.value}</span>
                        </label>
                      ))}
                    </div>
                    <div className="edit-actions">
                      <button className="btn-save" onClick={saveEditWarning}>Save</button>
                      <button className="btn-cancel" onClick={cancelEditWarning}>Cancel</button>
                    </div>
                  </>
                ) : (
                  warning.warning
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

(window as any).NPSDWarningsReact = NPSDWarnings;
